/**
 * useAutoAnalysis.js
 *
 * AI 자동 분석 기능을 위한 커스텀 훅.
 * 화면별로 중복되던 자동 분석 로직을 단일 훅으로 통합.
 *
 * @example
 * ```js
 * const { pageContext, handleAutoAnalysis, ChatWidgetComponent } = useAutoAnalysis({
 *   pageId: 'BF_REVIEW',
 *   autoAnalysisPrompt: '[자동분석]',
 *   buildContextConfig: {
 *     filters: () => ({ custHrchy, itemHrchy, ... }),
 *     gridData: () => metricDatas.slice(0, 10),
 *     chartData: () => chartDataFiltered
 *   },
 *   dependencies: [metricDatas, chartData]
 * });
 *
 * // 버튼에서 호출
 * <IconButton onClick={handleAutoAnalysis}>
 *   <AutoAwesomeIcon />
 * </IconButton>
 *
 * // 위젯 렌더링
 * {ChatWidgetComponent}
 * ```
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';
import ChatWidget from '@plannel/components/ChatWidget';

/**
 * @typedef {Object} BuildContextConfig
 * @property {Function} filters - 필터 정보를 반환하는 함수
 * @property {Function} gridData - 그리드 데이터를 반환하는 함수
 * @property {Function} [chartData] - 차트 데이터를 반환하는 함수 (선택)
 * @property {Function} [metadata] - 추가 메타데이터를 반환하는 함수 (선택)
 */

/**
 * @typedef {Object} UseAutoAnalysisOptions
 * @property {string} pageId - 화면 ID (예: 'BF_REVIEW', 'DP_WORKBENCH')
 * @property {string} [autoAnalysisPrompt='[자동분석]'] - 자동 분석 트리거 프롬프트
 * @property {BuildContextConfig} buildContextConfig - pageContext 구성 설정
 * @property {Array} [dependencies=[]] - pageContext 재빌드 의존성 배열
 * @property {boolean} [enabled=true] - 자동 분석 활성화 여부
 */

/**
 * AI 자동 분석 기능을 제공하는 커스텀 훅
 *
 * @param {UseAutoAnalysisOptions} options
 * @returns {{
 *   pageContext: Object,
 *   autoAnalysisTrigger: number,
 *   handleAutoAnalysis: Function,
 *   ChatWidgetComponent: JSX.Element
 * }}
 */
const useAutoAnalysis = ({
  pageId,
  autoAnalysisPrompt = '[자동분석]',
  buildContextConfig,
  dependencies = [],
  enabled = true,
}) => {
  const [autoAnalysisTrigger, setAutoAnalysisTrigger] = useState(0);
  const [pageContext, setPageContext] = useState({});

  /**
   * buildContextConfig 함수들을 실행하여 pageContext 생성
   */
  const buildPageContext = useCallback(() => {
    if (!buildContextConfig) return {};

    const context = {};

    // filters 생성
    if (buildContextConfig.filters) {
      context.filters = buildContextConfig.filters();
    }

    // gridData 생성
    if (buildContextConfig.gridData) {
      const gridData = buildContextConfig.gridData();
      context.gridData = Array.isArray(gridData) ? gridData.slice(0, 20) : gridData;
      context.gridTotalRows = Array.isArray(gridData) ? gridData.length : 0;
    }

    // chartData 생성 (선택)
    if (buildContextConfig.chartData) {
      const chartData = buildContextConfig.chartData();
      context.chartData = Array.isArray(chartData) ? chartData.slice(0, 30) : chartData;
      context.chartTotalRows = Array.isArray(chartData) ? chartData.length : 0;
    }

    // metadata 추가 (선택)
    if (buildContextConfig.metadata) {
      context.metadata = buildContextConfig.metadata();
    }

    return context;
  }, [buildContextConfig]);

  /**
   * 의존성 변경 시 pageContext 자동 갱신
   */
  useEffect(() => {
    if (!enabled) return;

    const newContext = buildPageContext();

    // gridData 또는 chartData가 있을 때만 갱신
    const hasData = !isEmpty(newContext.gridData) || !isEmpty(newContext.chartData);
    if (hasData) {
      setPageContext(newContext);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  /**
   * 자동 분석 버튼 핸들러
   */
  const handleAutoAnalysis = useCallback(() => {
    if (!enabled) {
      console.warn('[useAutoAnalysis] 자동 분석이 비활성화되어 있습니다.');
      return;
    }

    // pageContext 강제 갱신
    const newContext = buildPageContext();
    setPageContext(newContext);

    // ChatWidget 트리거 (autoAnalysisTrigger 증가)
    setAutoAnalysisTrigger(prev => prev + 1);
  }, [enabled, buildPageContext]);

  /**
   * ChatWidget 컴포넌트 (메모이제이션)
   */
  const ChatWidgetComponent = useMemo(() => {
    if (!enabled) return null;

    return (
      <ChatWidget
        pageId={pageId}
        autoAnalysisPrompt={autoAnalysisPrompt}
        pageContext={pageContext}
        autoAnalysisTrigger={autoAnalysisTrigger}
      />
    );
  }, [pageId, autoAnalysisPrompt, pageContext, autoAnalysisTrigger, enabled]);

  return {
    pageContext,
    autoAnalysisTrigger,
    handleAutoAnalysis,
    ChatWidgetComponent,
  };
};

export default useAutoAnalysis;

